import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TestFavoritesState } from '@/store/types/testFavoritesTypes';
import { addTestToFavorites, removeTestFromFavorites, testFavoriteSet } from './myTestsSlice';
import type { AppDispatch, RootState } from '@/store/store';

const initialState: TestFavoritesState = { byId: {} };

/**
 * Признак «в избранном» по testId для одиночных мест (кнопки на странице
 * теста и в шапке результата). Обновления приходят cross-slice `testFavoriteSet`
 * (из myTestsSlice) — optimistic и откат при ошибке. Начальное серверное
 * состояние заносится `testFavoriteHydrate` (см. ниже).
 */
const testFavoritesSlice = createSlice({
    name: 'testFavorites',
    initialState,
    reducers: {
        /**
         * Гидрация начального состояния из серверного пропа — заносим значение
         * ТОЛЬКО если для теста его ещё нет в сторе. Иначе не затираем более
         * свежее клиентское: острова делят один store, и пользователь мог
         * добавить тест в избранное на карточке, а затем открыть его результат
         * инлайн (тот же store) — там проп initialIsFavorite устаревший.
         */
        testFavoriteHydrate(state, action: PayloadAction<{ id: number; isFavorite: boolean }>) {
            if (state.byId[action.payload.id] === undefined) {
                state.byId[action.payload.id] = action.payload.isFavorite;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(testFavoriteSet, (state, action) => {
            state.byId[action.payload.id] = action.payload.isFavorite;
        });
    },
});

export const { testFavoriteHydrate } = testFavoritesSlice.actions;

/**
 * Тоггл избранного: по текущему состоянию выбирает направление и делегирует
 * optimistic-thunk'ам из myTestsSlice (они дёргают API + testFavoriteSet).
 */
export const toggleTestFavorite = (id: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    const isFavorite = getState().testFavorites.byId[id] ?? false;
    return dispatch(isFavorite ? removeTestFromFavorites(id) : addTestToFavorites(id));
};

export default testFavoritesSlice.reducer;
